import React, { useState, useEffect } from 'react';
import inventoryService, { Inventory } from '../../services/inventoryService';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface InventorySearchModalProps {
  supplierId: string;
  isOpen: boolean;
  onClose: () => void;
  onSelect: (inventory: Inventory) => void;
}

const InventorySearchModal: React.FC<InventorySearchModalProps> = ({
  supplierId,
  isOpen,
  onClose,
  onSelect,
}) => {
  const [inventories, setInventories] = useState<Inventory[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [category, setCategory] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchInventories = async () => {
    if (!supplierId) return;

    try {
      setLoading(true);
      const response = await inventoryService.search({
        supplierId,
        search: searchTerm,
        category: category || undefined,
        status: 'active',
        page,
        limit: 10,
      });
      setInventories(response.data);
      setTotalPages(response.totalPages);
    } catch (err: any) {
      console.error('Failed to fetch inventories:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && supplierId) {
      fetchInventories();
    }
  }, [isOpen, supplierId, searchTerm, category, page]);

  const handleSelect = (inventory: Inventory) => {
    onSelect(inventory);
    onClose();
    setSearchTerm('');
    setCategory('');
    setPage(1);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        ></div>

        {/* Modal panel */}
        <div className="relative inline-block w-full max-w-4xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Select Inventory Item</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Search Bar */}
          <div className="mb-4">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by item name, code, or description..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(1);
                }}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>

          {/* Inventory List */}
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center p-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              </div>
            ) : inventories.length === 0 ? (
              <div className="text-center p-8">
                <p className="text-gray-600">
                  {searchTerm || category
                    ? 'No inventory items found matching your search'
                    : 'No active inventory items available for this supplier'}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {inventories.map((inventory) => (
                  <div
                    key={inventory._id}
                    onClick={() => handleSelect(inventory)}
                    className="p-4 border border-gray-200 rounded-lg hover:border-indigo-500 hover:bg-indigo-50 cursor-pointer transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900">{inventory.itemName}</h4>
                        <div className="mt-1 flex items-center space-x-4 text-sm text-gray-600">
                          <span>Code: {inventory.itemCode}</span>
                          <span>•</span>
                          <span>Category: {inventory.category}</span>
                          <span>•</span>
                          <span>Available: {inventory.availableQuantity} {inventory.unit}</span>
                        </div>
                        {inventory.description && (
                          <p className="mt-1 text-sm text-gray-500 line-clamp-1">
                            {inventory.description}
                          </p>
                        )}
                        {inventory.minOrderQuantity && (
                          <p className="mt-1 text-xs text-gray-500">
                            Min Order: {inventory.minOrderQuantity} {inventory.unit}
                          </p>
                        )}
                      </div>
                      <div className="ml-4 text-right">
                        <p className="text-lg font-semibold text-indigo-600">
                          ${inventory.unitPrice.toFixed(2)}
                        </p>
                        <p className="text-xs text-gray-500">per {inventory.unit}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between border-t border-gray-200 pt-4">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-sm text-gray-700">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page === totalPages}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InventorySearchModal;
